/**
 * Cloud Quota Status
 *
 * Shows cached cloud tier/token/request bars with sync button.
 * Displays staleness warning when cache is old.
 *
 * @module @humanizer/studio/components/settings/CloudQuotaStatus
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocalAdmin } from '../../hooks/useLocalAdmin';

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function CloudQuotaStatus() {
  const { getCachedQuota, syncCloudQuota } = useLocalAdmin();

  const [quota, setQuota] = useState<any>(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    (async () => {
      // Try cached quota first
      const cached = await getCachedQuota();
      if (cached && !cached.error) {
        setQuota(cached);
        return;
      }
      // No cache — auto-sync from cloud
      setSyncing(true);
      const result = await syncCloudQuota();
      if (result?.error) {
        setError(result.error);
      } else if (result) {
        setQuota(result);
      }
      setSyncing(false);
    })();
  }, [getCachedQuota, syncCloudQuota]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError(null);
    const result = await syncCloudQuota();
    if (result?.error) {
      setError(result.error);
    } else if (result) {
      setQuota(result);
    }
    setSyncing(false);
  }, [syncCloudQuota]);

  if (!quota) {
    return (
      <div className="settings-card">
        <h3 className="settings-card__title">Cloud Quota</h3>
        <div className="settings-card__content">
          {error ? (
            <p className="settings-error-text">{error}</p>
          ) : syncing ? (
            <p className="settings-empty-text">Syncing with cloud...</p>
          ) : (
            <p className="settings-empty-text">
              Quota data not yet synced. Connect a cloud account in Settings, then sync.
            </p>
          )}
          <button
            className="settings-button settings-button--secondary"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      </div>
    );
  }

  const tokenPercent = quota.tokensLimit > 0
    ? Math.round((quota.tokensUsed / quota.tokensLimit) * 100) : 0;
  const requestPercent = quota.requestsLimit > 0
    ? Math.round((quota.requestsUsed / quota.requestsLimit) * 100) : 0;
  const cachedTime = new Date(quota.cachedAt).toLocaleString();

  const progressModifier = (pct: number) =>
    pct >= 90 ? '--danger' : pct >= 75 ? '--warning' : '';

  return (
    <div className="settings-card">
      <h3 className="settings-card__title">Cloud Quota</h3>
      <div className="settings-card__content">
        <div className="usage-stats">
          <div className="usage-stat">
            <span className="usage-stat__value">{quota.tier}</span>
            <span className="usage-stat__label">Tier</span>
          </div>
          <div className="usage-stat">
            <span className="usage-stat__value">{quota.billingPeriod}</span>
            <span className="usage-stat__label">Period</span>
          </div>
        </div>

        {/* Token Progress */}
        <div className="usage-progress">
          <div className="usage-progress__header">
            <span>Tokens: {quota.tokensUsed.toLocaleString()}</span>
            <span>Limit: {quota.tokensLimit.toLocaleString()}</span>
          </div>
          <div className="usage-progress__bar">
            <div
              className={`usage-progress__fill${progressModifier(tokenPercent)}`}
              style={{ width: `${Math.min(tokenPercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Request Progress */}
        <div className="usage-progress">
          <div className="usage-progress__header">
            <span>Requests: {quota.requestsUsed.toLocaleString()}</span>
            <span>Limit: {quota.requestsLimit.toLocaleString()}</span>
          </div>
          <div className="usage-progress__bar">
            <div
              className={`usage-progress__fill${progressModifier(requestPercent)}`}
              style={{ width: `${Math.min(requestPercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Cache status */}
        <div className="usage-breakdown">
          <div className="usage-breakdown__item">
            <span className="usage-breakdown__label">Last synced</span>
            <span className="usage-breakdown__value">
              {cachedTime}
              {quota.isStale && <span className="usage-stat__badge--warning"> (stale)</span>}
            </span>
          </div>
        </div>

        {error && (
          <p className="settings-error-text">{error}</p>
        )}

        <button
          className="settings-button settings-button--secondary"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing...' : 'Refresh Quota'}
        </button>
      </div>
    </div>
  );
}
